/**
 * Shared error-handling utilities for SportForm AI frontend.
 *
 * Handles the three historical response shapes from the backend, plus the
 * new standardized ErrorResponse shape added in API Foundation Phase 2:
 *
 *   1. Standardized (new):  { error, message, suggestion?, fields?, detail }
 *   2. Object-shape legacy: { detail: { error, message, ... } }
 *   3. String-shape legacy: { detail: "string" }
 *   4. JS Error / unknown:  err.message or fallback
 *
 * Use getErrorMessage() as the drop-in replacement for all local copies of
 * getErrorMessage() that were duplicated across LoginPage, RegisterPage,
 * ChannelManagePage, CreateChannelPage, PerTemplateAthleteTable, etc.
 */

import type { AxiosError } from 'axios';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Wire shape returned by the standardized error handler in main.py. */
export interface ErrorResponseShape {
  /** Stable machine-readable code (e.g. "not_found", "validation_failed"). */
  error?: string;
  /** Human-readable message safe to show to the user. */
  message?: string;
  /** Optional next-step suggestion. */
  suggestion?: string;
  /** Field-level validation context (422 only). */
  fields?: Record<string, string>;
  /**
   * Backwards-compat field — mirrors the original exc.detail verbatim:
   *   - string when the raise used detail="..."
   *   - dict   when the raise used detail={...}
   */
  detail?: string | Record<string, unknown> | unknown[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Extract a human-readable error message from any axios error or thrown value.
 *
 * Resolution priority:
 *   1. Standardized shape: response.data.message
 *   2. Object-detail legacy: response.data.detail.message
 *   3. String-detail legacy: response.data.detail (string)
 *   4. Plain JS Error: err.message
 *   5. fallback string
 */
export function getErrorMessage(err: unknown, fallback = 'An unexpected error occurred.'): string {
  if (!err) return fallback;

  const axiosErr = err as AxiosError<ErrorResponseShape>;
  if (axiosErr.response?.data) {
    const data = axiosErr.response.data;

    // Plain string body (rare)
    if (typeof data === 'string') return data;

    // 1. Standardized shape — prefer top-level message
    if (data.message && typeof data.message === 'string') return data.message;

    // 2. Object-shape legacy — detail is a dict with a message key
    if (
      data.detail &&
      typeof data.detail === 'object' &&
      !Array.isArray(data.detail) &&
      'message' in data.detail
    ) {
      const msg = (data.detail as Record<string, unknown>).message;
      if (typeof msg === 'string') return msg;
    }

    // 3. String-shape legacy — detail is a plain string
    if (typeof data.detail === 'string') return data.detail;
  }

  // 4. Plain JS Error
  if (err instanceof Error) return err.message || fallback;

  return fallback;
}

/**
 * Get the optional suggestion from a standardized error response.
 *
 * Useful for showing actionable next-step guidance, e.g.:
 *   "Change your password at POST /api/auth/me/password"
 */
export function getErrorSuggestion(err: unknown): string | undefined {
  const axiosErr = err as AxiosError<ErrorResponseShape>;
  const suggestion = axiosErr.response?.data?.suggestion;
  return typeof suggestion === 'string' ? suggestion : undefined;
}

/**
 * Get the stable machine-readable error code from a standardized response.
 *
 * Use for UI branching, e.g.:
 *   if (getErrorCode(err) === 'password_change_required') navigate('/me/password')
 */
export function getErrorCode(err: unknown): string | undefined {
  const axiosErr = err as AxiosError<ErrorResponseShape>;
  const data = axiosErr.response?.data;
  if (data && typeof data === 'object' && 'error' in data) {
    const code = (data as ErrorResponseShape).error;
    return typeof code === 'string' ? code : undefined;
  }
  return undefined;
}

/**
 * Get field-level validation errors from a 422 standardized response.
 *
 * Returns a map of field path → error message, or undefined if not present.
 */
export function getFieldErrors(err: unknown): Record<string, string> | undefined {
  const axiosErr = err as AxiosError<ErrorResponseShape>;
  const fields = axiosErr.response?.data?.fields;
  return fields && typeof fields === 'object' ? fields : undefined;
}
